/**
 * The composers' gate (slice 1b spec §4.5): nothing is asked until the AI is
 * connected. The composers read `/api/me` (already cached by the guard) and
 * are disabled with "Connect your AI to start" until then.
 */
import { TextLink } from "@crew/ui/components/typography";
import { cn } from "@crew/ui/lib/utils";
import { errorHint } from "../../app/api";
import type { AskGate } from "./api";

/**
 * Under a composer: "Connect your AI to start" with the way there while the
 * gate is closed (for a member, "<Owner> hasn't connected the AI yet."),
 * else a failed send's hint.
 */
export function AskNotes({
  gate,
  error,
  className,
}: {
  gate: AskGate;
  error: unknown;
  className?: string;
}) {
  // The composer's strip says it (slice 1j §6.1): only a send's hint shows here.
  if (gate.notice) return error ? <SendError error={error} className={className} /> : null;
  if (!gate.open && gate.owner) {
    // A member can't connect it (slice 6a §2): who can, and no link.
    return (
      <p data-ask-gate className={cn("text-meta text-muted-foreground", className)}>
        {gate.owner} hasn't connected the AI yet.
      </p>
    );
  }
  if (!gate.open && gate.href) {
    return (
      <p data-ask-gate className={cn("text-meta text-muted-foreground", className)}>
        Connect your AI to start. <TextLink href={gate.href}>{gateLinkText(gate.href)}</TextLink>
      </p>
    );
  }
  if (!error) return null;
  return <SendError error={error} className={className} />;
}

function SendError({ error, className }: { error: unknown; className?: string }) {
  return (
    <p role="alert" className={cn("text-meta text-brick", className)}>
      {errorHint(error)}
    </p>
  );
}

function gateLinkText(href: string): string {
  return href === "/setup" ? "Connect it" : "Settings → AI";
}
